import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { Router } from '@angular/router';
import { Auth, JwtPayload, LoginRequest } from '@business/auth/types/auth';
import { AuthResponse, UserProfileResponse } from '@business/users-management/types/users-management';
import { Permission } from '@constants/routing-permissions/permissions';
import { environment } from '@environment/environment';
import { jwtDecode } from 'jwt-decode';

const LOCAL_STORAGE_AUTH_KEY = 'auth';

const AuthEndpoints = {
  LOGIN: 'api/v1/auth/login',
  LOGOUT: 'api/v1/auth/logout',
};

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private readonly router = inject(Router);
  private readonly httpClient = inject(HttpClient);

  private auth = signal<Auth | null>(null);
  readonly profile = signal<UserProfileResponse | null>(null);
  readonly isAuthenticated = computed(() => !!this.auth());

  autoLogin() {
    const token = localStorage.getItem(LOCAL_STORAGE_AUTH_KEY);
    if (token) {
      this.setAuth(token);
    }
  }

  setAuth(token: string) {
    localStorage.setItem(LOCAL_STORAGE_AUTH_KEY, token);
    const decoded = jwtDecode<JwtPayload>(token);

    this.auth.set({
      userId: decoded.sub,
      permissions: decoded.permissions,
    });
  }

  clearAuth() {
    localStorage.removeItem(LOCAL_STORAGE_AUTH_KEY);
    this.auth.set(null);
    this.router.navigate(['/']);
  }

  login(request: LoginRequest) {
    return this.httpClient.post<AuthResponse>(environment.origin + AuthEndpoints.LOGIN, request);
  }

  logout() {
    return this.httpClient.post(environment.origin + AuthEndpoints.LOGOUT, {});
  }

  hasOneOfPermissions(permission: Permission[]) {
    return !!permission.some((p) => this.auth()?.permissions.includes(p));
  }
}
